import { FileWriter } from '@/platform'
import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import { resolveFontDisplayNameFromStoredPath } from '@/features/project-font/runtime'
import { clampBitmapFontSize } from '@/features/bitmap-font/fontSize'

/** 位图字体生成目标：TTF 存储路径、运行时展示名、字号。 */
export type BitmapFontTarget = {
  /** 虚拟资源树中的字体存储路径（如 `FZZYJW`）。 */
  sourceFontName: string
  /** 写入 xfi 文件名与 list 的展示名（如 `方正综艺简体`）。 */
  packageFontName: string
  /** 栅格化字号。 */
  fontSize: number
}

function sanitizeFileName(name: string): string {
  return String(name || '').trim().replace(/[\\/:*?"<>|]/g, '_')
}

/**
 * 生成位图字体包文件名前缀：`展示名$字号`。
 * @param fontName 展示名
 * @param fontSize 字号
 */
export function toBitmapFontPackageKey(fontName: string, fontSize: number): string {
  return `${sanitizeFileName(fontName) || 'bitmap-font'}$${Math.max(1, Math.round(fontSize || 0))}`
}

/**
 * 解析剧情主字体规格。
 * @param project 当前工程
 * @param currentRoot 字体虚拟资源根
 */
export function resolveMainFontSpec(project: MainProject, currentRoot: DataFileMap | null): BitmapFontTarget {
  const sourceFontName = String(project.systemData.totalSetting.FontName || '').trim()
  const fontSize = clampBitmapFontSize(Number(project.systemData.totalSetting.FontSize || 30))
  return {
    sourceFontName,
    packageFontName: resolveFontDisplayNameFromStoredPath(currentRoot, sourceFontName),
    fontSize,
  }
}

/**
 * 解析存读档字体规格。
 * `CundangFontName` 为空或 `CundangFontSize` 为 0 时与剧情主字体对齐（运行时共用 xfi）。
 * @param project 当前工程
 * @param currentRoot 字体虚拟资源根
 */
export function resolveSaveLoadFontSpec(project: MainProject, currentRoot: DataFileMap | null): BitmapFontTarget {
  const main = resolveMainFontSpec(project, currentRoot)
  const rawSource = String(project.systemData.Sldata.CundangFontName || '').trim()
  const rawSize = Number(project.systemData.Sldata.CundangFontSize || 0)
  const sourceFontName = rawSource || main.sourceFontName
  const fontSize = rawSize > 0 ? clampBitmapFontSize(rawSize) : main.fontSize
  return {
    sourceFontName,
    packageFontName: resolveFontDisplayNameFromStoredPath(currentRoot, sourceFontName),
    fontSize,
  }
}

/**
 * 判断两个规格是否对应同一套 xfi/png（展示名 + 字号一致）。
 */
export function isSameBitmapFontPackageKey(
  a: Pick<BitmapFontTarget, 'packageFontName' | 'fontSize'>,
  b: Pick<BitmapFontTarget, 'packageFontName' | 'fontSize'>,
): boolean {
  return toBitmapFontPackageKey(a.packageFontName, a.fontSize) === toBitmapFontPackageKey(b.packageFontName, b.fontSize)
}

function findTargetByPackageKey(targets: BitmapFontTarget[], spec: Pick<BitmapFontTarget, 'packageFontName' | 'fontSize'>): BitmapFontTarget | null {
  const key = toBitmapFontPackageKey(spec.packageFontName, spec.fontSize)
  return targets.find((target) => toBitmapFontPackageKey(target.packageFontName, target.fontSize) === key) ?? null
}

/**
 * 收集测试/发布待生成的位图字体包列表。
 * 优先生成剧情包；存读档仅在展示名+字号与剧情不一致时追加独立包。
 */
export function collectBitmapFontTargets(project: MainProject, currentRoot: DataFileMap | null): BitmapFontTarget[] {
  const main = resolveMainFontSpec(project, currentRoot)
  const saveLoad = resolveSaveLoadFontSpec(project, currentRoot)
  const targets: BitmapFontTarget[] = []
  const seen = new Set<string>()
  const pushSpec = (spec: BitmapFontTarget) => {
    if (!spec.sourceFontName.trim()) return
    const key = toBitmapFontPackageKey(spec.packageFontName, spec.fontSize)
    if (seen.has(key)) return
    seen.add(key)
    targets.push({
      sourceFontName: spec.sourceFontName,
      packageFontName: spec.packageFontName,
      fontSize: spec.fontSize,
    })
  }
  pushSpec(main)
  if (!isSameBitmapFontPackageKey(main, saveLoad)) {
    pushSpec(saveLoad)
  }
  return targets
}

/**
 * 写出引擎 `font.list` 二进制内容。
 */
export function buildFontListBytes(project: MainProject, targets: BitmapFontTarget[], currentRoot: DataFileMap | null): Uint8Array {
  const writer = new FileWriter()
  const main = resolveMainFontSpec(project, currentRoot)
  const mainTarget = findTargetByPackageKey(targets, main)
  const mainFontName = String(mainTarget?.packageFontName || main.packageFontName)
  const mainFontSize = mainTarget?.fontSize ?? main.fontSize
  const sizeSet = new Set<number>([mainFontSize])
  for (const target of targets) {
    if (String(target.packageFontName || '').trim() === mainFontName.trim()) {
      sizeSet.add(clampBitmapFontSize(target.fontSize))
    }
  }
  const sizes = Array.from(sizeSet).sort((a, b) => a - b)
  writer.writeInt(sizes.length)
  writer.writeString(mainFontName)
  writer.writeInt(mainFontSize)
  for (const size of sizes) {
    if (size !== mainFontSize) writer.writeInt(size)
  }
  return writer.toUint8Array()
}

/**
 * 写出引擎 `saveload.list` 二进制内容；必须能命中已生成的字体包。
 */
export function buildSaveLoadListBytes(project: MainProject, targets: BitmapFontTarget[], currentRoot: DataFileMap | null): Uint8Array {
  const writer = new FileWriter()
  const saveLoad = resolveSaveLoadFontSpec(project, currentRoot)
  const matchedTarget = findTargetByPackageKey(targets, saveLoad)
  if (!matchedTarget) {
    throw new Error(`存读档位图字体包未生成：${saveLoad.packageFontName}$${saveLoad.fontSize}`)
  }
  writer.writeInt(1)
  writer.writeString(matchedTarget.packageFontName)
  writer.writeInt(matchedTarget.fontSize)
  return writer.toUint8Array()
}
